import * as React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { DropdownMenu, DropdownToggle, DropdownItem, UncontrolledDropdown } from 'reactstrap';
import { LineOfBusinessClient, LineOfBusinessModel, LineOfBusinessRefModel } from '../../../../../client/ApiClient';
import { DataCell, DataRow, DataTable } from '../../../../basic/DataTable';
import { RangePaginationState, RangePagination, paginate } from 'components/basic/Pagination';
import StageActionToolbar from '../generic/StageActionToolbar';
import { stagingPageUpdate, stagingPageCopyMappings, StagingPageCopyMappingsItem } from '../generic/Abstractions'
import { AssignRowStatus, RowStatus } from '../generic';
import DataListInput from 'components/basic/DataListInput';
import { filterColumns, sort } from '../generic/EntityFunctions';

import '../../../../../styles/Icons.scss';

interface LineOfBusinessStagingPageArgs {
    conversionId: string,
}

type LineOfBusinessStagingPageProps = RouteComponentProps<LineOfBusinessStagingPageArgs>;

interface LineOfBusinessStagingPageState {
    items: (LineOfBusinessModel
        & StagingPageCopyMappingsItem)[];
    filter: string;
    refIds: LineOfBusinessRefModel[];
    sort: boolean;
    sortColumn: string;
    mapped: boolean;
    notMapped: boolean;
    dropped: boolean;

    pagination: RangePaginationState;
    buttonLock: boolean;
    filteredColumn: string;
}

export class LineOfBusinessStagingPage extends React.Component<LineOfBusinessStagingPageProps, LineOfBusinessStagingPageState>{
    private client: LineOfBusinessClient = new LineOfBusinessClient()

    private readonly copyMappings = stagingPageCopyMappings(this);

    constructor(props: LineOfBusinessStagingPageProps) {
        super(props)
        this.state = {
            items: [],
            filter: '',
            refIds: [],
            sort: true,
            sortColumn: '',
            mapped: true,
            notMapped: true,
            dropped: true,

            pagination: {
                pageSize: 25,
                pageNumber: 0,
            },
            buttonLock: true,
            filteredColumn: '',
        }
    }

    componentDidMount() {
        this.getItems();
    }

    setDefaults = (entity: LineOfBusinessModel) => new LineOfBusinessModel({
        ...entity,
        destinationCode: this.destinationName(entity.referenceId, true),
        destinationDescription: this.destinationName(entity.referenceId, false)
    })

    getItems = () => {
        const items = this.client.getAll(this.props.match.params.conversionId, '')
        const refs = this.client.getRef(this.props.match.params.conversionId)
        Promise.all([items, refs])
            .then(result => {
                this.setState({
                    items: result[0]!,
                    refIds: result[1]!
                }, async () => {
                    if (await items && await refs) {
                        this.setState({
                            items: sort(this.state.items.map(this.setDefaults), this.state.sortColumn, this.state.sort, [])
                        });
                    }
                })

            })
            .catch(error => { });
    }

    put(entity: LineOfBusinessModel) {
        if (entity.referenceId === '') {
            entity.referenceId = undefined
        }
        stagingPageUpdate(this, this.client)
            .withSource('Line of Business', entity.sourceCode!)
            .update(entity)
            .catch(() => { })
    }

    destinationName = (refId: string | undefined, type: boolean) => {
        if (refId) {
            const destination = this.state.refIds.filter(ref => ref.id === refId);
            if (destination[0]) { return type ? destination[0].code : destination[0].description }
            else { return '' }
        } else {
            return ''
        }
    }

    handleSearch = (filter: string) => {
        this.setState({ filter })
    }

    handleChange = (item: LineOfBusinessModel, e: any) => {
        const dc = e.value;
        const id = e.target.name;

        const items = this.state.items;
        const i = this.state.items.findIndex(v => v.id === id);

        items[i].destinationDescription = this.destinationName(dc, false)
        items[i].referenceId = dc;

        this.setState({ items });
        this.put(items[i])
    }

    handleCheckboxChange = (id: string, e: { target: { name: any, checked: any } }) => {
        const items = this.state.items.map(
            v => v.id === id
                ? new LineOfBusinessModel({
                    ...v,
                    [e.target.name]: e.target.checked,
                })
                : v)
        this.setState({ items });

        this.put(items.find(v => v.id === id)!)
    }

    sort(column: string) {
        const sorted = this.state.sortColumn === column ? !this.state.sort : true;
        const items = sort(this.state.items, column, sorted, [])
        this.setState({ sort: sorted, sortColumn: column, items });
    }

    mappedCheck(check: string) {
        if (check === 'notMapped') {
            const notMapped = !this.state.notMapped
            this.setState({ notMapped })
        } else if (check === 'mapped') {
            const mapped = !this.state.mapped
            this.setState({ mapped })
        } else if (check === 'dropped') {
            const dropped = !this.state.dropped
            this.setState({ dropped })
        }
    }

    LineOfBusinessRefs = (): any[] => {
        const arr = [];
        const LineOfBusinessRefs = this.state.refIds;
        for (const [, LineOfBusinessRef] of LineOfBusinessRefs.entries()) {
            arr.push(<option
                key={LineOfBusinessRef.id}
                data-value={LineOfBusinessRef.id}
            >
                {`${LineOfBusinessRef.code} - ${LineOfBusinessRef.description}`}
            </option>)
        }
        return arr;

    }

    lockButton = (lock: boolean) => {
        this.setState({ buttonLock: lock })
    }

    selectFilteredColumn = (filteredColumn: string) => {
        this.setState({ filteredColumn })
    }

    public render() {
        const conversionId = this.props.match.params.conversionId;
        const filteredColumn = this.state.filteredColumn;
        const items = this.state.items
            .map(AssignRowStatus)
            .filter(i => ((filteredColumn === '' || filteredColumn === 'sourceCode') && i.sourceCode && i.sourceCode!.toLowerCase().includes(this.state.filter.trim().toLowerCase())) ||
                ((filteredColumn === '' || filteredColumn === 'sourceDescription') && i.sourceDescription && i.sourceDescription!.toLowerCase().includes(this.state.filter.trim().toLowerCase())) ||
                ((filteredColumn === '' || filteredColumn === 'destinationCode') && i.destinationCode && i.destinationCode!.toLowerCase().includes(this.state.filter.trim().toLowerCase())) ||
                ((filteredColumn === '' || filteredColumn === 'type') && i.type && i.type!.toLowerCase().includes(this.state.filter.trim().toLowerCase())) ||
                ((filteredColumn === '' || filteredColumn === 'destinationDescription') && i.destinationDescription && i.destinationDescription!.toLowerCase().includes(this.state.filter.trim().toLowerCase())))
            .filter(item =>
                (item.mapped === RowStatus.NotMapped && this.state.notMapped)
                || (item.mapped === RowStatus.Mapped && this.state.mapped)
                || (item.mapped === RowStatus.Dropped && this.state.dropped));
        return (
            <>
                <StageActionToolbar
                    conversionId={conversionId}
                    handleSearch={this.handleSearch}
                    disableMigrate={true}
                    handleCopyMapping={this.copyMappings.submitMappings}
                    sectionId={'lineofbusiness'}
                    mapExcel={true}
                    copyMap={true}
                    getItems={() => this.getItems()}
                    filter={this.state.filter}
                    lockButton={this.lockButton}
                    filterColumns={filterColumns(items)}
                    selectFilteredColumn={this.selectFilteredColumn}
              />
                <DataTable>
                    <thead>
                        <tr className='nonFullWidthRow'>
                            <th onClick={() => this.sort('sourceCode')}>Source Code<i className="fa fa-sort"></i></th>
                            <th onClick={() => this.sort('sourceDescription')}>Source Description<i className="fa fa-sort"></i></th>
                            <th onClick={() => this.sort('type')}>Type<i className="fa fa-sort"></i></th>
                            <th onClick={() => this.sort('isDrop')} className='checkbox-column'>Exclude<i className="fa fa-sort"></i></th>
                            <th onClick={() => this.sort('lobCount')} className='checkbox-column'>LOB Count<i className="fa fa-sort"></i></th>
                            <th onClick={() => this.sort('destinationCode')}>Destination Code<i className="fa fa-sort"></i></th>
                            <th className='checkbox-column'>
                                <UncontrolledDropdown setActiveFromChild className="ismappedDropdown">
                                    <DropdownToggle tag="a" className="ismapped" caret>
                                        Mapped
                                 </DropdownToggle>
                                    <DropdownMenu>
                                        <DropdownItem className={!this.state.notMapped ? 'mappedChecked' : ''} onClick={() => this.mappedCheck('notMapped')}>&#x274C; Not Mapped</DropdownItem>
                                        <DropdownItem className={!this.state.mapped ? 'mappedChecked' : ''} onClick={() => this.mappedCheck('mapped')}>&#x2705; Mapped</DropdownItem>
                                        <DropdownItem className={!this.state.dropped ? 'mappedChecked' : ''} onClick={() => this.mappedCheck('dropped')}>&#x2796; Excluded</DropdownItem>
                                    </DropdownMenu>
                                </UncontrolledDropdown>
                            </th>
                            <th onClick={() => this.sort('isUserMapped')} className='checkbox-column'>Auto-Mapped<i className="fa fa-sort"></i></th>
                        </tr>
                    </thead>
                    <tbody>
                        {
                            paginate(items, this.state.pagination, (item, index) => (
                                <DataRow id={item.id.toString()} key={item.id}>
                                    <DataCell>{item.sourceCode}</DataCell>
                                    <DataCell>{item.sourceDescription}</DataCell>
                                    <DataCell>{item.type}</DataCell>
                                    <DataCell className='checkbox-column'>
                                        <input className='custom-checkbox'
                                            type='checkbox'
                                            name='isDrop'
                                            checked={item.isDrop}
                                            onChange={e => this.handleCheckboxChange(item.id, e)}
                                            disabled={this.state.buttonLock}
                                        />
                                    </DataCell>
                                    <DataCell className='center-aligning'>{item.lobCount > 0 && item.lobCount}</DataCell>
                                    <DataCell>
                                        <DataListInput
                                            id={`referenceId - ${index}`}
                                            name={item.id.toString()}
                                            value={item.referenceId ? item.referenceId : undefined}
                                            onValueChange={event => this.handleChange(item, event)}
                                            disabled={this.state.buttonLock}
                                        >
                                            <option data-value='' />
                                            {this.LineOfBusinessRefs()}
                                        </DataListInput>
                                    </DataCell>

                                    <DataCell className='checkbox-column' itemProp={item.mapped === RowStatus.NotMapped ? 'unmapped' : item.mapped === RowStatus.Mapped ? 'mapped' : 'dropped'}>
                                        {item.mapped === RowStatus.NotMapped ? < span className='mapped' itemProp='unmapped'>&#x274C;</span> : item.mapped === RowStatus.Mapped ? < span className='mapped' itemProp='mapped'>&#x2705;</span> : < span className='mapped' itemProp='dropped'>&#x2796;</span>}
                                    </DataCell> 
                                    <DataCell
                                        className='checkbox-column'
                                        itemProp={!item.isUserMapped && item.isMapped ? 'auto-mapped' : ''}
                                    >
                                        {!item.isUserMapped && item.isMapped ? < span className='mapped' itemProp='auto-mapped'>&#x2705;</span> : ''}
                                    </DataCell>
                                </DataRow>
                            ))
                        }
                    </tbody>
                </DataTable>
                <RangePagination
                    pageNumber={this.state.pagination.pageNumber}
                    pageSize={this.state.pagination.pageSize}
                    recordsCount={items.length}
                    onChangeIndex={index => this.setState({
                        pagination: {
                            ...this.state.pagination,
                            pageNumber: index,
                        }
                    })}
                />
            </>
        );
    }
}